// Supabase client for reading public data (videos, comments)

import { createClient } from '@supabase/supabase-js'
import { env } from './env'
import type { Video, Comment } from '@/types'

// Only create client if credentials are available
const supabaseUrl = env.SUPABASE_URL
const supabaseAnonKey = env.SUPABASE_ANON_KEY

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null

/**
 * Get approved videos for the public feed
 */
export async function getApprovedVideos(limit = 20, offset = 0): Promise<Video[]> {
  if (!supabase) {
    console.warn('Supabase not configured')
    return []
  }

  const { data, error } = await supabase
    .from('videos')
    .select('*')
    .eq('status', 'approved')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) {
    console.error('Failed to fetch videos:', error)
    throw error
  }

  return data || []
}

/**
 * Get a single video by ID
 */
export async function getVideoById(id: string): Promise<Video | null> {
  if (!supabase) {
    console.warn('Supabase not configured')
    return null
  }

  const { data, error } = await supabase
    .from('videos')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      return null // Not found
    }
    console.error('Failed to fetch video:', error)
    throw error
  }

  return data
}

/**
 * Get comments for a video
 */
export async function getCommentsForVideo(videoId: string): Promise<Comment[]> {
  if (!supabase) {
    console.warn('Supabase not configured')
    return []
  }

  const { data, error } = await supabase
    .from('comments')
    .select('*')
    .eq('video_id', videoId)
    .eq('flagged', false)
    .order('created_at', { ascending: true })

  if (error) {
    console.error('Failed to fetch comments:', error)
    throw error
  }

  return data || []
}

/**
 * Get video URL from R2 key
 */
export function getVideoUrl(r2Key: string | null): string | null {
  if (!r2Key) return null
  const videosUrl = env.VIDEOS_URL
  if (!videosUrl) {
    // Fallback for development
    return `http://localhost:8787/${r2Key}`
  }
  return `${videosUrl}/${r2Key}`
}

/**
 * Get pending videos for admin review
 */
export async function getPendingVideos(): Promise<Video[]> {
  if (!supabase) {
    console.warn('Supabase not configured')
    return []
  }

  const { data, error } = await supabase
    .from('videos')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Failed to fetch pending videos:', error)
    throw error
  }

  return data || []
}

/**
 * Approve a video
 */
export async function approveVideo(id: string): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase not configured')
  }

  const { error } = await supabase
    .from('videos')
    .update({ status: 'approved', reviewed_at: new Date().toISOString() })
    .eq('id', id)

  if (error) {
    console.error('Failed to approve video:', error)
    throw error
  }
}

/**
 * Reject a video
 */
export async function rejectVideo(id: string, reason?: string): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase not configured')
  }

  const { error } = await supabase
    .from('videos')
    .update({
      status: 'rejected',
      reviewed_at: new Date().toISOString(),
      rejection_reason: reason || null
    })
    .eq('id', id)

  if (error) {
    console.error('Failed to reject video:', error)
    throw error
  }
}

/**
 * Add a comment to a video
 */
export async function addComment(videoId: string, body: string, sessionTokenHash: string): Promise<Comment> {
  if (!supabase) {
    throw new Error('Supabase not configured')
  }

  const { data, error } = await supabase
    .from('comments')
    .insert({
      video_id: videoId,
      body: body.trim(),
      session_token_hash: sessionTokenHash
    })
    .select()
    .single()

  if (error) {
    console.error('Failed to add comment:', error)
    throw error
  }

  return data
}

/**
 * Upvote a video (one per session)
 */
export async function upvoteVideo(videoId: string, sessionTokenHash: string): Promise<boolean> {
  if (!supabase) {
    throw new Error('Supabase not configured')
  }

  const { error } = await supabase
    .from('upvotes')
    .insert({
      video_id: videoId,
      session_token_hash: sessionTokenHash
    })

  if (error) {
    // Unique constraint violation = already upvoted
    if (error.code === '23505') {
      return false
    }
    console.error('Failed to upvote:', error)
    throw error
  }

  return true
}

/**
 * Check if session has upvoted a video
 */
export async function hasUpvoted(videoId: string, sessionTokenHash: string): Promise<boolean> {
  if (!supabase) {
    return false
  }

  const { data } = await supabase
    .from('upvotes')
    .select('id')
    .eq('video_id', videoId)
    .eq('session_token_hash', sessionTokenHash)
    .single()

  return !!data
}

/**
 * Get upvote status for multiple videos
 */
export async function getUpvoteStatuses(videoIds: string[], sessionTokenHash: string): Promise<Set<string>> {
  if (!supabase || !sessionTokenHash) {
    return new Set()
  }

  const { data } = await supabase
    .from('upvotes')
    .select('video_id')
    .eq('session_token_hash', sessionTokenHash)
    .in('video_id', videoIds)

  return new Set((data || []).map(u => u.video_id))
}
